import { useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { ButtonIcon } from "../../../common/components/Button";
import Navigation from "../../../common/components/Navigation/Navigation";
import { openSlider } from "../../../common/state/slice/modal/modalSlice";
import type { RootState } from "../../../common/state/store";
import { setAlert } from "../../alert/state/alertsSlice";
import { getNodeById } from "../../graph/state/graphLegacy";
import {
  getMediaSearch,
  getCurrentModeListGallery,
  isUploading,
  setCurrentModeListGallery,
  setUploadedFile,
} from "../state/gallerySlice";
import {
  computeUploadPanelParams,
  deduceMediaType,
  fromMediaTypeInferInputAcceptParam,
} from "../state/uploadUtil";
import { GENERATE_MEDIA_SLIDEOVER_COMPONENT } from "./GenerateMediaPanel";
import { MediaSearchPanelContainer } from "./MediaSearchPanel";
import { useLocation } from "react-router-dom";
import Alert from "../../../common/components/Alert/Alert";
import { ViewGridIcon, ViewListIcon } from "@heroicons/react/outline";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import { useIsModalGallery } from "src/common/util/isModalGallery";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { isGifMedia } from "../util/mediaTypeUtil";
import { useMediaUploadHelp } from "./MediaUploadHelpLink";
import HelpArticlePanel from "src/common/components/HelpArticleSection/HelpArticlePanel";

export const regexIsNodeContentScene = (pathname: string) => {
  return /courses\/\d+\/list\/\d+\/graph\/create\/scene/.test(pathname);
};

export const Nav = ({
  nodeId,
  title = "",
  allowedMediaTypeOverride,
  allowedMedia360TagOverride,
  hideGenerateButton = false,
  uploadAcceptOverride,
  onUploadedFileSelect,
  isModalGallery: isModalGalleryProp = false,
}: {
  nodeId: string;
  title?: string;
  allowedMediaTypeOverride?: string;
  allowedMedia360TagOverride?: string;
  isModalGallery?: boolean;
  hideGenerateButton?: boolean;
  uploadAcceptOverride?: string;
  onUploadedFileSelect?: (file: File) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isModalGallery = useIsModalGallery() || isModalGalleryProp;

  const currentMediaFilter = useAppSelector(getMediaSearch);
  const uploading = useAppSelector(isUploading);
  const currentUser = useAppSelector(getCurrentUser);
  const currentModeListGallery = useAppSelector(getCurrentModeListGallery);

  const uploadHelp = useMediaUploadHelp();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [inputKey, setInputKey] = useState(Date.now());

  const currentNode = useAppSelector((state: RootState) =>
    Number(nodeId) !== 0 ? getNodeById(state, nodeId) : null,
  );

  const isNodeContentScene = currentNode?.modelName === "content-scene";

  let fileType = "all";
  if (currentNode) {
    if (currentNode.modelName === "content-scene" && currentMediaFilter?.mediaType !== "model3d") {
      fileType = "content-scene";
    } else {
      fileType = currentMediaFilter?.mediaType;
    }
  }
  if (regexIsNodeContentScene(pathname)) {
    fileType = "content-scene";
  }
  const alertExtension = (e: ChangeEvent<HTMLInputElement>, isGif = false) => {
    e.target.value = "";
    dispatch(
      setAlert({
        msg: isGif ? t("general.extensionNotAcceptedGif") : t("general.extensionNotAccepted"),
        type: "danger",
        isOpen: true,
      }),
    );
  };

  const isNodeContentSceneOrRegex = isNodeContentScene || regexIsNodeContentScene(pathname);

  return (
    <>
      {isNodeContentSceneOrRegex && (
        <div className="z-50 text-center">
          <Alert />
        </div>
      )}
      <Navigation
        title={title}
        buttons={
          <>
            {currentUser?.role?.name === "Creator" && (
              <>
                <div className="relative group w-full">
                  <ButtonIcon
                    styled="btn-primary-fill w-full"
                    text={t("pages.project.importMedia")}
                    icon="CloudUploadIcon"
                    onClick={() => {
                      hiddenFileInput.current?.click();
                    }}
                    disabled={uploading}
                  />
                  <div className="absolute right-0 top-full pt-1 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
                    <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-72 shadow-lg whitespace-normal font-normal">
                      {t("pages.gallery.uploadHelp.summary")}
                      {uploadHelp.ready && (
                        <>
                          {" "}
                          <button
                            type="button"
                            onClick={() => uploadHelp.setOpen(true)}
                            className="font-bold underline underline-offset-4 text-white"
                          >
                            {uploadHelp.learnMore}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <HelpArticlePanel
                  title={uploadHelp.title}
                  article={uploadHelp.article}
                  open={uploadHelp.open}
                  onClose={() => uploadHelp.setOpen(false)}
                />
                <input
                  id="upload-input"
                  key={inputKey}
                  type="file"
                  ref={hiddenFileInput}
                  style={{ display: "none" }}
                  accept={
                    uploadAcceptOverride ?? fromMediaTypeInferInputAcceptParam(fileType) ?? "image"
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const extension = file.name.slice(file.name.lastIndexOf("."));

                    const testIfExtensionIsGif = () => {
                      if (isNodeContentSceneOrRegex) {
                        return isGifMedia({ type: file.type, filename: file.name });
                      }
                      return false;
                    };

                    let currentFileType = file.type;
                    if (isStringNullOrEmpty(currentFileType)) {
                      const splitFileName = file.name.split(".");
                      if (splitFileName.length > 0) {
                        try {
                          const type = deduceMediaType(splitFileName[splitFileName.length - 1]);
                          currentFileType = type + "/" + splitFileName[splitFileName.length - 1];
                        } catch {
                          currentFileType = "";
                        }
                      }
                    }

                    const splitInArray = (
                      uploadAcceptOverride ?? fromMediaTypeInferInputAcceptParam(fileType)
                    ).split(",");
                    const testExtensions = !splitInArray.includes(currentFileType);

                    if (testExtensions || extension === ".m4v" || testIfExtensionIsGif()) {
                      alertExtension(e, testIfExtensionIsGif());
                    } else if (onUploadedFileSelect) {
                      onUploadedFileSelect(file);
                      setInputKey(Date.now());
                    } else {
                      const cupp = computeUploadPanelParams({ file, from: "graph" });
                      dispatch(setUploadedFile({ file, metadata: cupp }));
                      setInputKey(Date.now());
                    }
                  }}
                ></input>
              </>
            )}

            {currentUser?.role?.name === "Creator" && !hideGenerateButton && (
              <div>
                <ButtonIcon
                  styled="btn-alternative-outline w-full mt-[0.4rem] mb-[0.4rem]"
                  text={t("pages.gallery.generateMediaPanel.title")}
                  icon="PuzzleIcon"
                  onClick={() => {
                    dispatch(openSlider(GENERATE_MEDIA_SLIDEOVER_COMPONENT));
                  }}
                  disabled={uploading}
                />
              </div>
            )}
            {!isModalGallery && (
              <button
                id="gallery-list-mode-toggle"
                className="w-7 flex justify-end mb-1 ml-44"
                onClick={() => dispatch(setCurrentModeListGallery(!currentModeListGallery))}
              >
                {currentModeListGallery ? (
                  <ViewGridIcon className="w-7 h-7 stroke-green-500 " />
                ) : (
                  <ViewListIcon className="w-7 h-7 stroke-green-500 " />
                )}
              </button>
            )}
          </>
        }
        tagsBar={
          <MediaSearchPanelContainer
            nodeId={nodeId}
            allowedMediaTypeOverride={allowedMediaTypeOverride}
            allowedMedia360TagOverride={allowedMedia360TagOverride}
          />
        }
      />
    </>
  );
};
